import React from 'react'
import withSheet from 'App/helpers/withSheet'
import autobind from 'autobind-decorator'
import PropTypes from 'prop-types'
import moment from 'moment'
import formatCurrency from 'App/helpers/formatCurrency'
import {withApollo} from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import styles from './styles.css'

const query = gql`
  query getOrderDetails($orderId: ID!) {
    order(orderId: $orderId) {
      _id
      sell
      type
      amount
      closedAt
      amountToHold
      secondaryAmount
      filled
      secondaryFilled
      limitPrice
      createdAt
      isStop
      status
      stopPriceUp
      stopPriceDown
      trades {
        _id
        amount
        price
        totalCost
        date
      }
      market {
        name
        code
        mainCurrency {
          code
          format
          longFormat
          units
        }
        secondaryCurrency {
          code
          format
          longFormat
          units
        }
      }
    }
  }
`
@withApollo
@withSheet
export default class Details extends React.Component {
  static propTypes = {
    client: PropTypes.object,
    showSheet: PropTypes.func,
    setRef: PropTypes.func
  }

  componentDidMount() {
    this.props.setRef(this)
  }

  getStopDetails(order) {
    const {market} = order
    const {secondaryCurrency} = market
    if (!order.isStop) return []
    const up = order.stopPriceUp
      ? formatCurrency(order.stopPriceUp, secondaryCurrency, {code: true})
      : 'No tiene'
    const down = order.stopPriceDown
      ? formatCurrency(order.stopPriceDown, secondaryCurrency, {code: true})
      : 'No tiene'
    return [
      {label: 'Precio de activación superior', value: up},
      {label: 'Precio de activación inferior', value: down}
    ]
  }

  getStatus(order) {
    if (order.status === 'open') return 'Abierta'
    if (order.status === 'closed') return 'Cerrada'
    if (order.status === 'stop') return 'Esperando activación'
    return order.status
  }

  getAmount(order) {
    const {market} = order
    const {secondaryCurrency, mainCurrency} = market
    const opts = {code: true}
    return order.secondaryAmount
      ? formatCurrency(order.secondaryAmount, secondaryCurrency, opts)
      : formatCurrency(order.amount || 0, mainCurrency, opts)
  }

  getPrice(order) {
    const {market} = order
    const {secondaryCurrency} = market
    if (order.type === 'market') {
      return 'Mercado'
    }
    return formatCurrency(order.limitPrice, secondaryCurrency, {code: true})
  }

  getType(order) {
    const text = order.sell ? 'Venta' : 'Compra'
    if (!order.isStop) return text
    return `${text} (stop)`
  }

  getFilled(order) {
    const {market} = order
    const {mainCurrency, secondaryCurrency} = market
    const opts = {code: true}
    return order.secondaryAmount
      ? formatCurrency(order.secondaryFilled || 0, secondaryCurrency, opts)
      : formatCurrency(order.filled || 0, mainCurrency, opts)
  }

  getAmountToHold(order) {
    const {market, amountToHold} = order
    const {mainCurrency, secondaryCurrency} = market
    const opts = {code: true}
    const currency = order.sell ? mainCurrency : secondaryCurrency
    return formatCurrency(amountToHold || 0, currency, opts)
  }

  renderTrades(order) {
    if (!order.trades.length) return
    const {market} = order
    const {mainCurrency, secondaryCurrency} = market
    const opts = {code: true}

    return (
      <div className={styles.transactions}>
        <div className={styles.title}>Transacciones</div>
        <table className={styles.txTable} cellSpacing={0}>
          <thead>
            <tr>
              <th>Monto</th>
              <th>Precio</th>
              <th>Total</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {order.trades.map(trade => (
              <tr key={trade._id}>
                <td>{formatCurrency(trade.amount, mainCurrency, opts)}</td>
                <td>{formatCurrency(trade.price, secondaryCurrency, opts)}</td>
                <td>{formatCurrency(trade.totalCost, secondaryCurrency, opts)}</td>
                <td>{moment(trade.date).format('HH:mm:ss DD MMM')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  @autobind
  async getOrder(orderId) {
    const {
      data: {order}
    } = await this.props.client.query({
      variables: {
        orderId
      },
      query
    })

    return order
  }

  @autobind
  async open(orderId) {
    const order = await this.getOrder(orderId)
    const {market} = order
    await this.props.showSheet({
      title: 'Detalles',
      values: [
        {label: 'Mercado', value: market.name},
        {label: 'Tipo', value: this.getType(order)},
        {label: 'Estado', value: this.getStatus(order)},
        ...this.getStopDetails(order),
        {label: 'Precio límite', value: this.getPrice(order)},
        {label: 'Cantidad', value: this.getAmount(order)},
        {label: 'Completado', value: this.getFilled(order)},
        {label: 'Monto retenido', value: this.getAmountToHold(order)},
        {label: 'Fecha de creación', value: moment(order.createdAt).format('LL HH:mm:ss.SSS')},
        {
          label: 'Fecha de cierre',
          value: order.closedAt ? moment(order.closedAt).format('LL HH:mm:ss.SSS') : 'Pendiente'
        }
      ],
      bottom: this.renderTrades(order)
    })
  }

  render() {
    return <span />
  }
}
